import { PayloadAction, createSlice } from "@reduxjs/toolkit";

import { Document } from "../types/document";
import { ImageProps } from "../types/image";
import { MonitorService } from "../types/monitor";
import { InfestationType } from "../types/service";

export type MonitorSliceState = {
  inProgress: boolean;
  propertyId: string;
  location: string;
  comment: string;
  isTemporary: boolean;
  isWireless: boolean;
  wirelessId: string | null;
  monitorTypes: MonitorTypes;
  monitorPosition: MonitorPosition;
  monitor: MonitorService;
  images: ImageProps[];
  trapType: InfestationType | null;
};

export type MonitorTypes = {
  monitorType: string;
  propertyArea: string;
  propertyAreaId: string;
  monitors: MonitorService[];
  map: Document | null;
};

type MonitorPosition = {
  x: number;
  y: number;
};

const initialState: MonitorSliceState = {
  inProgress: false,
  propertyId: "",
  location: "",
  comment: "",
  isTemporary: false,
  isWireless: false,
  wirelessId: null,
  monitorTypes: {
    monitorType: "",
    propertyArea: "",
    propertyAreaId: "",
    monitors: [],
    map: null,
  },
  monitorPosition: {
    x: 1,
    y: 1,
  },
  monitor: {
    id: "",
    isActive: true,
    monitorType: "",
    monitorTypeId: "",
    isTemporary: false,
    isWireless: false,
    wirelessId: null,
    position: 0,
    location: "",
    x: 1,
    y: 1,
    infestation: null,
    trapType: null,
  },
  images: [],
  trapType: null,
};

export const creatingMonitorSlice = createSlice({
  name: "monitor",
  initialState: initialState,
  reducers: {
    setInProgress(state, action: PayloadAction<boolean>) {
      state.inProgress = action.payload;
    },
    setPropertyId(state, action: PayloadAction<string>) {
      state.propertyId = action.payload;
    },
    setLocation(state, action: PayloadAction<{ location: string }>) {
      const location = action.payload.location;
      state.location = location;
    },
    setIsTemporary(state: MonitorSliceState) {
      state.isTemporary = !state.isTemporary;
    },
    setIsWireless(state: MonitorSliceState) {
      state.isWireless = !state.isWireless;
    },
    setWirelessId(
      state,
      { payload }: PayloadAction<{ wirelessId: string | null }>
    ) {
      state.wirelessId = payload.wirelessId;
    },
    setComment(state, { payload }: PayloadAction<{ comment: string }>) {
      state.comment = payload.comment;
    },
    setMonitorTypes(state, { payload }: PayloadAction<MonitorTypes>) {
      state.monitorTypes = payload;
    },
    setMonitorPosition(state, { payload }: PayloadAction<MonitorPosition>) {
      state.monitorPosition = payload;
    },
    setMonitorInfoForServices(
      state,
      { payload }: PayloadAction<MonitorService>
    ) {
      state.monitor = payload;
    },
    addImage(state, { payload }: PayloadAction<ImageProps>) {
      state.images.push(payload);
    },
    removeImage(state, { payload }: PayloadAction<string>) {
      state.images = state.images.filter((image) => image.uuid !== payload);
    },
    setTrapType(state, action: PayloadAction<InfestationType | null>) {
      state.trapType = action.payload;
    },
    resetState(state) {
      return {
        ...initialState,
        monitor: state.monitor,
        monitorTypes: state.monitorTypes,
      };
    },
  },
});

export const creatingMonitorActions = creatingMonitorSlice.actions;
